import { useState, useCallback, useMemo } from 'react'
import {
  ApplicationBodyContainer,
  ApplicationBodyContent,
  DefaultTitle,
  ClaimsContainer
} from '../../../components/application/defaultComponents'
import PrebuiltRenewalFooter from '../../../components/application/PrebuiltRenewalFooter'
import { useRenewal } from '../../../context/renewal'
import ICOCItemForm from 'src/components/application/formComponents/ICOCItemForm'
import { AttorneyData } from 'src/dtos/applicationData'
import { ValidationError } from 'yup'
import produce from 'immer'
import { generateRandomNumberId } from 'src/utils/math'
import { allActiveAttorneys, onlyICOCAttorneys } from 'src/utils/renewals'
import { RenewalData } from 'src/dtos/renewalData'
import { FirmAttorneysICOCSchema } from 'src/yup/RenewalSchema'

interface AttorneyDataWithId extends AttorneyData {
  id: number;
}

const RenewalsICOC = () => {
  /*
    0 - EC93 - FirstName
    1 - EC94 - LastName
    2 - EC95 - YearStartedAsICOC
    3 - EC96 - HoursOnBill
    4 - EC97 - HoursBilledInFirstYear
    5 - EC98 - ActsIndependentlyFromFirm
    6 - EC99 - ActsIndependentlyFromFirmWhere
    7 - EC103 - HasInsuranceForIndependentPractice
    8 - EC104 - EffectivePolicyDateFrom
    9 - EC105 - EffectivePolicyDateTo
  */
  const renewalICOCErrorCodes = ['EC93', 'EC94', 'EC95', 'EC96', 'EC97', 'EC98', 'EC99', 'EC103', 'EC104', 'EC105']
  const [valErrors, setValErrors] = useState<any[]>()
  const { renewalData } = useRenewal()
  const [attorneys, setAttorneys] = useState<AttorneyDataWithId[]>(() => {
    const icocAttorneys = onlyICOCAttorneys(renewalData)

    return icocAttorneys.map(att => ({ ...att, id: generateRandomNumberId() }))
  }); 

  const [showErrors, setShowErrors] = useState(false);

  const updateAttorneys = useCallback((index: number, data: Partial<AttorneyDataWithId>) => {
    setAttorneys(produce(draft => { draft[index] = { ...draft[index], ...data } }))
  }, []);

  const updatedApplicationData = (): RenewalData => {
    let olderAttorneys = [...(renewalData?.attorneysSection?.attorneys ?? [])]
    let newAttorneys = [...(renewalData?.renewalSection?.newAttorneys ?? [])]

    if (olderAttorneys.length > 0) {
      olderAttorneys = olderAttorneys.map(att => {
        const attorneyFound = attorneys.find(icoc => icoc.firstName === att.firstName && icoc.lastName === att.lastName)
        if (attorneyFound) {
          const { id, ...rest } = attorneyFound
          return rest
        }
        return att
      })
    }

    if (newAttorneys.length > 0) {
      newAttorneys = newAttorneys.map(att => {
        const attorneyFound = attorneys.find(icoc => icoc.firstName === att.firstName && icoc.lastName === att.lastName)
        if (attorneyFound) {
          const { id, ...rest } = attorneyFound
          return rest
        }
        return att
      })
    }

    return {
      ...renewalData,
      currentPage: window.location.pathname,
      attorneysSection: {
        ...renewalData?.attorneysSection,
        attorneys: olderAttorneys
      },
      renewalSection: {
        ...renewalData?.renewalSection,
        newAttorneys
      }
    }
  }

  const validatePage = useMemo(() => {
    const validations = attorneys.map((attorney, index) => {
      try {
        FirmAttorneysICOCSchema.validateSync(attorney, { abortEarly: false });
        return [];
      } catch (e) {
        const { errors } = e as ValidationError;
        const filteredErrors = errors.filter(err => err !== 'typeerror');
        
        return `${filteredErrors}:${index}`;
      }
    }).flat();

    setValErrors(validations);
    if (validations.length > 0) return false;

    return true
  }, [attorneys])

  const scrollPageIfInvalid = () => {
    if (!validatePage) {
      for (let attorney of attorneys) {
        try {
          FirmAttorneysICOCSchema.validateSync(attorney, { abortEarly: false })
        } catch (err) {
          const { errors } = err as ValidationError
          if (!(errors.length === 1 && errors.includes('typeerror'))) {
            setShowErrors(true)
            document.getElementById(`${[attorney?.firstName, attorney?.lastName].join('').replaceAll(' ', '_')}__${errors[0]}`)?.scrollIntoView({ behavior: 'smooth' })
            break
          }
        }
      }
    }
  }

  const goBackRoute = () => {
    const allAttorneys = allActiveAttorneys(updatedApplicationData())

    if (allAttorneys.length === 1) {
      return '/customer/renew/attorneys/sole-practitioner'
    }

    return '/customer/renew/attorneys/verify'
  }

  const goNextRoute = () => {
    return '/customer/renew/questions/main'
  }

  return (
    <ApplicationBodyContainer>
      <ApplicationBodyContent width='80%'>
        <DefaultTitle>Please fill out the following regarding independent contractors and of counsel attorneys.</DefaultTitle>
        <ClaimsContainer style={{ marginTop: '12px' }}>
          <span className='claims-form-header-subtitle'>
            Please complete a separate supplement for each attorney who acts as "Of Counsel"
            of and "Independent Contractor" for your firm.
            <br />
            If you do not know, leave blank.
          </span>
          {attorneys.map((attorney, index) => (
            <ICOCItemForm
              key={attorney.id}
              attorney={attorney}
              attorneyIndex={index}
              setAttorney={data => updateAttorneys(index, data)}
              isIncorrect={!validatePage && showErrors}
              errorCodes={renewalICOCErrorCodes.map(e => `${e}:${index}`)}
              validationErrors={valErrors}
            />
          ))}
        </ClaimsContainer>
      </ApplicationBodyContent>
      <PrebuiltRenewalFooter
        goBackRoute={goBackRoute()}
        isFormValid={validatePage}
        nextRoute={goNextRoute()}
        updatedApplication={updatedApplicationData()}
        beforeSaveContinueClick={scrollPageIfInvalid}
      />
    </ApplicationBodyContainer>
  )
}

export default RenewalsICOC